import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppFacadeService } from '../../services/app-facade.service';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { PathRoutingService } from '../../services/path-routing.service';

/**
 * AssetTagComponent — Public product detail view via asset tag URL.
 *
 * Standalone route: /assetTag/?AssetTagId=X
 * No authentication required. OrgId is resolved inside the NgRx effect
 * via OrganizationIdService.getOrgIdFromUrl().
 *
 * Error UX: Dedicated inline error page (not toast). Error title and message
 * are derived from the error token stored in NgRx state.
 *
 * SEMANTIC INVARIANT: No PII or store-specific data in error messages.
 * Generic contact guidance only.
 */
@Component({
    standalone: false,
    selector: 'por-asset-tag',
    templateUrl: './asset-tag.component.html',
    styleUrls: ['./asset-tag.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetTagComponent implements OnInit, OnDestroy {
    readonly appFacadeService = inject(AppFacadeService);
    private readonly featureToggleService = inject(FeatureToggleService);

    assetTagId = '';

    /** Tracks featureDisabled and invalidRequest flags reactively for OnPush compatibility. */
    private readonly componentState$ = new BehaviorSubject<{ featureDisabled: boolean; invalidRequest: boolean }>({
        featureDisabled: false,
        invalidRequest: false
    });

    readonly isLoading$ = this.appFacadeService.getProductLoading();
    readonly hasError$ = this.appFacadeService.getProductError();

    /** True when content should be shown: feature enabled, no invalid request, and no error. */
    readonly showContent$ = combineLatest([this.componentState$, this.hasError$]).pipe(map(([state, error]) => !state.featureDisabled && error === null && !state.invalidRequest));

    readonly errorTitle$ = combineLatest([this.componentState$, this.hasError$]).pipe(
        map(([state, error]) => {
            if (state.featureDisabled) return 'assetTagFeatureDisabledTitle';
            if (state.invalidRequest) return 'assetTagErrorInvalidTitle';
            switch (error?.token) {
                case 'AssetTagNotSupported':
                    return 'assetTagErrorNotSupportedTitle';
                case 'AssetTagNotFound':
                    return 'assetTagErrorNotFoundTitle';
                case 'OrgNotFound':
                    return 'assetTagErrorOrgTitle';
                default:
                    return 'assetTagErrorServiceTitle';
            }
        })
    );

    readonly errorMessage$ = combineLatest([this.componentState$, this.hasError$]).pipe(
        map(([state, error]) => {
            if (state.featureDisabled) return 'assetTagFeatureDisabledMessage';
            if (state.invalidRequest) return 'assetTagErrorInvalidMessage';
            switch (error?.token) {
                case 'AssetTagNotSupported':
                    return 'assetTagErrorNotSupportedMessage';
                case 'AssetTagNotFound':
                    return 'assetTagErrorNotFoundMessage';
                case 'OrgNotFound':
                    return 'assetTagErrorOrgMessage';
                default:
                    return 'assetTagErrorServiceMessage';
            }
        })
    );

    private readonly destroy$ = new Subject<void>();

    ngOnInit(): void {
        if (!this.featureToggleService.isAvailable('assetTags')) {
            this.componentState$.next({ featureDisabled: true, invalidRequest: false });
            return;
        }

        this.assetTagId = PathRoutingService.getQueryParam('AssetTagId') || '';

        if (!this.assetTagId) {
            this.componentState$.next({ featureDisabled: false, invalidRequest: true });
            return;
        }

        this.appFacadeService.loadPublicProductByAssetTag(this.assetTagId);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
